import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormHelpers, showErrorSwal } from '@asamco/utils';
import { BehaviorSubject, Observable, map } from 'rxjs';
import Swal from 'sweetalert2';
import { CompanyType } from '../../../enums/company-type.enum';
import { Company } from '../../../interfaces/company.interface';
import { AlCompanyService } from '../../../services/al-company.service';
import { AsyncPipe, NgIf } from '@angular/common';
import { DxTemplateModule } from 'devextreme-angular/core';
import { DxoSortingModule, DxoPagingModule, DxoSearchPanelModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { AsValidatorDirective } from '../../../../../../../base/src/lib/as-validator/as-validator.directive';
import { DxTextBoxModule, DxSelectBoxModule, DxDataGridModule, DxCheckBoxModule } from 'devextreme-angular';
import { CompanyCreationFlow } from '../../../enums/company-creation-flow.enum';

@Component({
	selector: 'app-shell-admin-company-creator',
	templateUrl: './admin-company-creator.component.html',
	styleUrls: ['./admin-company-creator.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		FormsModule,
		NgIf,
		ReactiveFormsModule,
		DxTextBoxModule,
		DxCheckBoxModule,
		AsValidatorDirective,
		DxSelectBoxModule,
		DxDataGridModule,
		DxoSortingModule,
		DxoPagingModule,
		DxoSearchPanelModule,
		DxiColumnModule,
		DxTemplateModule,
		AsyncPipe,
	],
})
export class AdminCompanyCreatorComponent implements OnInit {
	companyForm = new FormGroup({
		name: new FormControl('', [Validators.required]),
		fullIdentifier: new FormControl(''),
		companyType: new FormControl(''),
		creationFlow: new FormControl(CompanyCreationFlow.Standalone),
		parentCompanyId: new FormControl(''),
		initOwnerEmail: new FormControl(''),
	});

	public customErrors = {
		ERROR_REQUIRED: 'This field is required',
		ERROR_MAXLENGTH: 'This field exceeds the maximum allowed amount of characters',
	};

	public listOfCompanies$: BehaviorSubject<Company[]> = new BehaviorSubject<Company[]>([]);

	// companies selectable as parent companies for sub-companies
	public listOfParentCompanies$: Observable<Company[]>;

	CompanyCreationFlow = CompanyCreationFlow;

	companyTypes: any[] = [
		{
			name: CompanyType[CompanyType.Default],
			id: CompanyType.Default,
		},
		{
			name: CompanyType[CompanyType.Microsoft],
			id: CompanyType.Microsoft,
		},
		{
			name: CompanyType[CompanyType.AppRiver],
			id: CompanyType.AppRiver,
		},
		{
			name: CompanyType[CompanyType.Inty],
			id: CompanyType.Inty,
		},
	];

	public readonly creationFlows = [
		{
			name: 'Standalone',
			id: CompanyCreationFlow.Standalone,
		},
		{
			name: 'Sub-company to...',
			id: CompanyCreationFlow.SubCompany,
		},
	];

	constructor(private companyService: AlCompanyService) {}

	ngOnInit(): void {
		this.listOfParentCompanies$ = this.listOfCompanies$.pipe(map(comps => comps.filter(c => !c.parentCompanyId)));

		this.getCompanies();

		this.setValidators();
	}

	setValidators() {
		if (this.companyForm.controls.creationFlow.value === CompanyCreationFlow.Standalone) {
			this.companyForm.controls.fullIdentifier.setValidators([Validators.required]);
			this.companyForm.controls.companyType.setValidators([Validators.required]);
			this.companyForm.controls.parentCompanyId.clearValidators();
		} else {
			this.companyForm.controls.fullIdentifier.clearValidators();
			this.companyForm.controls.companyType.clearValidators();
			this.companyForm.controls.parentCompanyId.setValidators([Validators.required]);
		}
	}

	getCompanies() {
		this.companyService.getAllCompanies().subscribe({
			next: companies => this.listOfCompanies$.next(companies),
			error: showErrorSwal,
		});
	}

	getCompanyEnumValue(companyTypeValue: number) {
		return CompanyType[companyTypeValue];
	}

	createCompany() {
		if (!FormHelpers.validateForm(this.companyForm)) {
			Swal.fire({ icon: 'warning', title: 'Please check if all fields are filled in' });
			return;
		}

		this.companyService.createCompany(this.companyForm.value).subscribe({
			next: e => {
				Swal.fire({
					icon: 'success',
					title: 'Successfully created company',
					text: `Company with name ${e.name}, identifier ${e.fullIdentifier} and ID ${e.id} has been created`,
				});
				this.getCompanies();
			},
			error: showErrorSwal,
		});
	}

	errorTest() {
		const object = {};
		const niks = (<any>object).keiHarde.error;
	}
}
